import {
  Heading,
  InputMonth,
  InputNumber,
  InputRadio,
  InputSelect,
} from "components/v2";

import {
  APPROVAL_FLOW_KEYS,
  APPROVAL_FLOW_VALUES,
} from "../../constants/approvalFlow";
import { LEAVE_TYPES_STRINGS } from "../../constants/strings";

import type { CreateLeaveTypeFormValuesT } from "../../domain/types/leaveType.types";

type FormStepTwoValuesT = Pick<
  CreateLeaveTypeFormValuesT,
  | "approvalFlow"
  | "hasBalanceControl"
  | "annualDays"
  | "maxDaysPerRequest"
  | "resetMonth"
  | "requiresAttachment"
  | "advanceNoticeRequirement"
  | "minAdvanceNoticeDays"
  | "allowBackdateDays"
  | "maxBackdateDays"
>;

type FormStepTwoPropsT = {
  values: FormStepTwoValuesT;
  errors: Partial<Record<keyof FormStepTwoValuesT, string>>;
  handleChange: (e: React.ChangeEvent<unknown>) => void;
};

const APPROVAL_FLOW_OPTIONS = Object.values(APPROVAL_FLOW_KEYS).map(
  (value) => ({
    label: APPROVAL_FLOW_VALUES[value],
    value,
  }),
);

const FormStepTwo = ({ values, errors, handleChange }: FormStepTwoPropsT) => {
  return (
    <div className="flex flex-col w-full p-2 gap-2">
      <InputSelect
        id="approval-flow"
        name="approvalFlow"
        label={LEAVE_TYPES_STRINGS.APPROVAL_FLOW_LABEL}
        placeholder={LEAVE_TYPES_STRINGS.APPROVAL_FLOW_PLACEHOLDER}
        options={APPROVAL_FLOW_OPTIONS}
        optionLabel="label"
        optionValue="value"
        value={values.approvalFlow}
        onChange={handleChange}
        error={errors?.approvalFlow}
        isRequired
      />
      <InputNumber
              id="max-days-per-request"
              name="maxDaysPerRequest"
              label={LEAVE_TYPES_STRINGS.MAX_DAYS_PER_REQUEST_LABEL}
              placeholder={LEAVE_TYPES_STRINGS.MAX_DAYS_PER_REQUEST_PLACEHOLDER}
              suffixText={LEAVE_TYPES_STRINGS.DAYS_TEXT}
              value={values.maxDaysPerRequest}
              onChange={handleChange}
              error={errors?.maxDaysPerRequest}
              isRequired
            />  
      <InputRadio
        label={LEAVE_TYPES_STRINGS.HAS_BALANCE_CONTROL_LABEL}
        name="hasBalanceControl"
        checkedValue={values.hasBalanceControl}
        onChange={handleChange}
        error={errors?.hasBalanceControl}
        radios={[
          {
            id: "balance-control-yes",
            label: LEAVE_TYPES_STRINGS.HAS_BALANCE_CONTROL_YES,
            value: "yes",
          },
          {
            id: "balance-control-no",
            label: LEAVE_TYPES_STRINGS.HAS_BALANCE_CONTROL_NO,
            value: "no",
          },
        ]}
        isRequired
        inline
      />
      {values.hasBalanceControl === "yes" && (
        <>
          <div className="flex gap-2">
            <InputNumber
              id="annual-days"
              name="annualDays"
              label={LEAVE_TYPES_STRINGS.ANNUAL_DAYS_LABEL}
              placeholder={LEAVE_TYPES_STRINGS.ANNUAL_DAYS_PLACEHOLDER}
              suffixText={LEAVE_TYPES_STRINGS.DAYS_TEXT}
              value={values.annualDays}
              onChange={handleChange}
              error={errors?.annualDays}
              isRequired
            />
          </div>
          <InputMonth
            id="reset-month"
            name="resetMonth"
            label={LEAVE_TYPES_STRINGS.RESET_MONTH_LABEL}
            helperText={LEAVE_TYPES_STRINGS.RESET_MONTH_HELPER}
            value={values.resetMonth}
            onChange={handleChange}
            error={errors?.resetMonth}
            className="!w-[calc(50%_-_.5rem)]"
            isRequired
          />
        </>
      )}
      <InputRadio
        label={LEAVE_TYPES_STRINGS.REQUIRES_ATTACHMENT_LABEL}
        name="requiresAttachment"
        checkedValue={values.requiresAttachment}
        onChange={handleChange}
        error={errors?.requiresAttachment}
        radios={[
          {
            id: "requires-attachment-yes",
            label: LEAVE_TYPES_STRINGS.REQUIRES_ATTACHMENT_YES,
            value: "yes",
          },
          {
            id: "requires-attachment-no",
            label: LEAVE_TYPES_STRINGS.REQUIRES_ATTACHMENT_NO,
            value: "no",
          },
        ]}
        isRequired
        inline
      />
      <Heading level={2} className="mt-2">
        {LEAVE_TYPES_STRINGS.HEADER_DEADLINE_TO_REQUEST}
      </Heading>
      <div className="flex flex-col gap-1">
        <InputRadio
          label={LEAVE_TYPES_STRINGS.ADVANCE_NOTICE_LABEL}
          name="advanceNoticeRequirement"
          checkedValue={values.advanceNoticeRequirement}
          onChange={handleChange}
          error={errors?.advanceNoticeRequirement}
          radios={[
            {
              id: "advance-notice-none",
              label: LEAVE_TYPES_STRINGS.RADIO_ADVANCE_NOTICE_NONE,
              value: "none",
            },
            {
              id: "advance-notice-required",
              label: LEAVE_TYPES_STRINGS.RADIO_ADVANCE_NOTICE_REQUIRED,
              value: "required",
            },
          ]}
          isRequired
        />
        {values.advanceNoticeRequirement === "required" && (
          <InputNumber
            id="min-advance-notice-days"
            name="minAdvanceNoticeDays"
            label={LEAVE_TYPES_STRINGS.INPUT_ADVANCE_NOTICE_LABEL}
            placeholder={LEAVE_TYPES_STRINGS.INPUT_ADVANCE_NOTICE_PLACEHOLDER}
            suffixText={LEAVE_TYPES_STRINGS.DAYS_TEXT}
            value={values.minAdvanceNoticeDays}
            onChange={handleChange}
            error={errors?.minAdvanceNoticeDays}
            className="!w-56"
            helperText={LEAVE_TYPES_STRINGS.INPUT_ADVANCE_NOTICE_HELPER}
            isRequired
          />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <InputRadio
          label={LEAVE_TYPES_STRINGS.ALLOW_BACKDATE_LABEL}
          name="allowBackdateDays"
          checkedValue={values.allowBackdateDays}
          onChange={handleChange}
          error={errors?.allowBackdateDays}
          radios={[
            {
              id: "allow-backdate-no",
              label: LEAVE_TYPES_STRINGS.RADIO_PAST_DATES_NO,
              value: "no",
            },
            {
              id: "allow-backdate-yes",
              label: LEAVE_TYPES_STRINGS.RADIO_PAST_DATES_YES,
              value: "yes",
            },
          ]}
          isRequired
        />
        {values.allowBackdateDays === "yes" && (
          <InputNumber
            id="max-backdate-days"
            name="maxBackdateDays"
            label={LEAVE_TYPES_STRINGS.INPUT_PAST_DATES_LABEL}
            placeholder={LEAVE_TYPES_STRINGS.INPUT_PAST_DATES_PLACEHOLDER}
            suffixText={LEAVE_TYPES_STRINGS.DAYS_TEXT}
            value={values.maxBackdateDays}
            onChange={handleChange}
            error={errors?.maxBackdateDays}
            className="!w-56"
            helperText={LEAVE_TYPES_STRINGS.INPUT_PAST_DATES_HELPER}
            isRequired
          />
        )}
      </div>
    </div>
  );
};

export default FormStepTwo;
